import React, { useRef, useState } from "react";
import { Film, Upload, X, Loader2, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "./ImageUploader";

const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const MAX_BYTES = 50 * 1024 * 1024;

/**
 * <MultiVideoUploader value={[url,...]} onChange={(urls)=>...} max={3} />
 * Drop or click to upload .mp4/.webm/.mov clips up to 50MB. Renders a thumbnail
 * grid with native <video> previews + remove buttons. Mirrors MultiImageUploader.
 */
export default function MultiVideoUploader({ value = [], onChange, max = 3, testid = "video" }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    const remaining = Math.max(0, max - (value?.length || 0));

    const handleFiles = async (files) => {
        if (!files?.length) return;
        if (remaining <= 0) {
            toast.error(`Maximum ${max} videos.`);
            return;
        }
        const accepted = Array.from(files).slice(0, remaining);
        for (const f of accepted) {
            if (!VIDEO_TYPES.includes(f.type)) {
                toast.error(`${f.name}: unsupported type. Use mp4/webm/mov.`);
                return;
            }
            if (f.size > MAX_BYTES) {
                toast.error(`${f.name}: too large (max 50MB).`);
                return;
            }
        }
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of accepted) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads/video", fd, {
                    headers: { "Content-Type": undefined },
                });
                uploaded.push(data.url);
            }
            onChange?.([...(value || []), ...uploaded]);
            toast.success(`Uploaded ${uploaded.length} clip${uploaded.length > 1 ? "s" : ""}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = (i) => {
        const next = (value || []).filter((_, idx) => idx !== i);
        onChange?.(next);
    };

    return (
        <div data-testid={`${testid}-uploader`} className="space-y-3">
            {value?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {value.map((url, i) => (
                        <div key={url + i} data-testid={`${testid}-thumb-${i}`} className="relative aspect-video rounded-md overflow-hidden bg-midnight-pine border border-ridge-line group">
                            <video
                                src={resolveImageUrl(url)}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                                controls
                            />
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                aria-label="Remove"
                                data-testid={`${testid}-remove-${i}`}
                                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {remaining > 0 && (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testid}-add-btn`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed border-ridge-line text-slate-gray hover:border-ember-orange hover:text-ember-orange transition-all disabled:opacity-50 bg-trail-tan/15"
                >
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                    <span className="text-sm font-bold">
                        {uploading ? "Uploading…" : `Add video clips (${remaining} left, mp4/webm/mov, max 50MB each)`}
                    </span>
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                multiple
                hidden
                data-testid={`${testid}-input`}
                onChange={(e) => handleFiles(e.target.files)}
            />
        </div>
    );
}
